import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import customFetch from '../../../api/customFetch.js';

export default function RegulationStats() {
  const { id } = useParams();
  const nav = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSector, setExpandedSector] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'read' | 'unread'
  const [searchName, setSearchName] = useState('');

  useEffect(() => {
    loadStats();
  }, [id]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const { data: res } = await customFetch.get(`/regulations/admin/${id}/stats`);
      setData(res);
    } catch {
      setError('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>טוען נתוני קריאה...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-page">
        <div className="admin-alert error"><span>⚠️</span><span>{error}</span></div>
      </div>
    );
  }

  const { regulation, stats, bySector } = data;

  const allEmployees = bySector.flatMap((s) => s.employees);
  const filteredEmployees = allEmployees.filter((emp) => {
    if (filterStatus === 'read' && !emp.hasRead) return false;
    if (filterStatus === 'unread' && emp.hasRead) return false;
    if (searchName && !emp.fullName.includes(searchName)) return false;
    return true;
  });

  const ringColor = stats.overallPercentage >= 80
    ? 'var(--admin-success)'
    : stats.overallPercentage >= 50
    ? 'var(--admin-warning)'
    : 'var(--admin-danger)';

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="page-title-icon">📊</span>
          מעקב קריאה: {regulation.title}
        </h1>
        <div className="page-actions">
          <button className="admin-btn secondary" onClick={() => nav('/admin/regulations')}>
            ← חזור לרשימה
          </button>
          <button className="admin-btn info" onClick={() => nav(`/admin/regulations/${id}`)}>
            ✏️ ערוך תקנון
          </button>
          <button className="admin-btn secondary" onClick={loadStats}>
            🔄 רענן
          </button>
        </div>
      </div>

      {/* Overall Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {/* Progress Ring Card */}
        <div className="admin-card" style={{ textAlign: 'center', padding: 30 }}>
          <svg viewBox="0 0 120 120" style={{ width: 120, height: 120, transform: 'rotate(-90deg)' }}>
            <circle cx="60" cy="60" r="50" fill="none" stroke="var(--admin-border)" strokeWidth="10" />
            <circle
              cx="60" cy="60" r="50" fill="none"
              stroke={ringColor}
              strokeWidth="10"
              strokeDasharray={`${2 * Math.PI * 50}`}
              strokeDashoffset={`${2 * Math.PI * 50 * (1 - stats.overallPercentage / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
          </svg>
          <div style={{ marginTop: -10 }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: ringColor }}>{stats.overallPercentage}%</div>
            <div style={{ color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>אחוז קריאה כללי</div>
          </div>
        </div>

        <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: 30 }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--admin-success)' }}>{stats.totalRead}</div>
          <div style={{ color: 'var(--admin-text-muted)' }}>קראו</div>
        </div>

        <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: 30 }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--admin-danger)' }}>{stats.totalUnread}</div>
          <div style={{ color: 'var(--admin-text-muted)' }}>לא קראו</div>
        </div>

        <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: 30 }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--admin-primary)' }}>{stats.totalEmployees}</div>
          <div style={{ color: 'var(--admin-text-muted)' }}>סה"כ עובדים</div>
        </div>
      </div>

      {/* By Sector */}
      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title">📈 סטטיסטיקות לפי סקטור</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {bySector.map((sector) => {
            const pct = sector.percentage;
            const color = pct >= 80 ? 'var(--admin-success)' : pct >= 50 ? 'var(--admin-warning)' : 'var(--admin-danger)';
            const isExpanded = expandedSector === sector.sector;

            return (
              <div key={sector.sector} style={{ border: '1px solid var(--admin-border)', borderRadius: 10, overflow: 'hidden' }}>
                {/* Sector Header */}
                <div
                  style={{ padding: '14px 18px', cursor: 'pointer', background: 'var(--admin-card-hover)', display: 'flex', alignItems: 'center', gap: 16 }}
                  onClick={() => setExpandedSector(isExpanded ? null : sector.sector)}
                >
                  <div style={{ fontWeight: 600, color: 'var(--admin-text)', minWidth: 120 }}>🏢 {sector.sector}</div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--admin-text-muted)' }}>{sector.read} / {sector.total} קראו</span>
                      <span style={{ color, fontWeight: 700 }}>{pct}%</span>
                    </div>
                    <div style={{ height: 8, background: 'var(--admin-border)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className={`badge ${sector.unread === 0 ? 'success' : 'warning'}`}>
                      {sector.unread === 0 ? '✅ הושלם' : `${sector.unread} ממתינים`}
                    </span>
                    <span style={{ color: 'var(--admin-text-muted)' }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded Employee List */}
                {isExpanded && (
                  <div style={{ padding: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--admin-border)' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--admin-text-muted)', fontWeight: 600 }}>שם עובד</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--admin-text-muted)', fontWeight: 600 }}>סטטוס</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--admin-text-muted)', fontWeight: 600 }}>תאריך קריאה</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sector.employees.map((emp) => (
                          <tr
                            key={emp._id}
                            style={{ borderBottom: '1px solid var(--admin-border)', background: emp.hasRead ? 'rgba(34, 197, 94, 0.05)' : 'rgba(239, 68, 68, 0.05)' }}
                          >
                            <td style={{ padding: '10px 12px', color: 'var(--admin-text)', fontWeight: 500 }}>{emp.fullName}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <span className={`badge ${emp.hasRead ? 'success' : 'danger'}`}>
                                {emp.hasRead ? '✅ קרא' : '❌ לא קרא'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>
                              {emp.readAt ? new Date(emp.readAt).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* All Employees Table - Filterable */}
      <div className="admin-card">
        <div className="card-header">
          <h3 className="card-title">👥 כל העובדים</h3>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <select
            className="admin-select"
            style={{ width: 'auto' }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">הכל ({allEmployees.length})</option>
            <option value="read">קראו ({stats.totalRead})</option>
            <option value="unread">לא קראו ({stats.totalUnread})</option>
          </select>

          <input
            type="text"
            className="admin-input"
            style={{ maxWidth: 240 }}
            placeholder="חיפוש לפי שם..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />

          <div style={{ marginRight: 'auto', fontSize: '0.85rem', color: 'var(--admin-text-muted)', alignSelf: 'center' }}>
            מציג {filteredEmployees.length} עובדים
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--admin-border)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--admin-text-muted)', fontWeight: 600 }}>שם עובד</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--admin-text-muted)', fontWeight: 600 }}>סקטור</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--admin-text-muted)', fontWeight: 600 }}>סטטוס</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--admin-text-muted)', fontWeight: 600 }}>תאריך קריאה</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 30, textAlign: 'center', color: 'var(--admin-text-muted)' }}>
                    לא נמצאו עובדים
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((emp) => (
                  <tr
                    key={emp._id}
                    style={{ borderBottom: '1px solid var(--admin-border)', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--admin-card-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '10px 12px', color: 'var(--admin-text)', fontWeight: 500 }}>{emp.fullName}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--admin-text-muted)' }}>{emp.sector}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <span className={`badge ${emp.hasRead ? 'success' : 'danger'}`}>
                        {emp.hasRead ? '✅ קרא' : '❌ לא קרא'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>
                      {emp.readAt
                        ? new Date(emp.readAt).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
